import React, { useCallback, useEffect, useRef, useState } from "react"
import { useSelector } from "react-redux"
import { FiBookOpen, FiCheck, FiLoader } from "react-icons/fi"

import { getLectureNote, saveLectureNote } from "../../../services/operations/courseDetailsAPI"

const DEBOUNCE_MS = 1200

export default function LectureNotes({ subSectionId, courseId }) {
  const { token } = useSelector((s) => s.auth)

  const [content,  setContent]  = useState("")
  const [status,   setStatus]   = useState("idle") // idle | saving | saved
  const [expanded, setExpanded] = useState(false)

  const debounceRef = useRef(null)
  const lastSaved   = useRef("")

  // Load note for this lecture
  useEffect(() => {
    if (!subSectionId) return
    setContent("")
    setStatus("idle")
    lastSaved.current = "";

    (async () => {
      const saved = await getLectureNote(subSectionId, token)
      setContent(saved)
      lastSaved.current = saved
      if (saved) setExpanded(true)
    })()
  }, [subSectionId])

  // Debounced auto-save
  const handleChange = useCallback(
    (e) => {
      const val = e.target.value
      setContent(val)
      setStatus("saving")

      clearTimeout(debounceRef.current)
      debounceRef.current = setTimeout(async () => {
        if (val === lastSaved.current) { setStatus("idle"); return }
        await saveLectureNote(subSectionId, courseId, val, token)
        lastSaved.current = val
        setStatus("saved")
        setTimeout(() => setStatus("idle"), 2000)
      }, DEBOUNCE_MS)
    },
    [subSectionId, courseId, token]
  )

  // Flush on unmount
  useEffect(() => () => clearTimeout(debounceRef.current), [])

  return (
    <div className="flex flex-col gap-2 rounded-2xl border border-richblack-700 bg-richblack-800">
      {/* Header — click to expand / collapse */}
      <button
        type="button"
        onClick={() => setExpanded((p) => !p)}
        className="flex items-center justify-between px-4 py-3"
      >
        <div className="flex items-center gap-2.5">
          <FiBookOpen size={15} className="text-yellow-50" />
          <span className="text-sm font-semibold text-richblack-100">My Notes</span>
          {content && (
            <span className="rounded-full bg-yellow-50/10 px-2 py-0.5 text-[10px] font-medium text-yellow-50">
              {content.split(/\s+/).filter(Boolean).length} words
            </span>
          )}
        </div>

        <div className="flex items-center gap-3">
          {status === "saving" && (
            <span className="flex items-center gap-1 text-xs text-richblack-400">
              <FiLoader size={11} className="animate-spin" /> Saving…
            </span>
          )}
          {status === "saved" && (
            <span className="flex items-center gap-1 text-xs text-caribbeangreen-300">
              <FiCheck size={11} /> Saved
            </span>
          )}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            className={`h-4 w-4 text-richblack-400 transition-transform duration-200 ${expanded ? "rotate-180" : ""}`}
          >
            <path
              fillRule="evenodd"
              d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z"
              clipRule="evenodd"
            />
          </svg>
        </div>
      </button>

      {/* Editor */}
      {expanded && (
        <div className="border-t border-richblack-700 px-4 pb-4 pt-3">
          <textarea
            value={content}
            onChange={handleChange}
            placeholder="Write your personal notes for this lecture… (auto-saved)"
            rows={6}
            className="w-full resize-y rounded-xl border border-richblack-600 bg-richblack-900 p-3 text-sm leading-relaxed text-richblack-100 placeholder-richblack-500 outline-none transition-colors focus:border-yellow-50/40"
          />
          <p className="mt-1.5 text-right text-xs text-richblack-500">
            Notes are private — only visible to you
          </p>
        </div>
      )}
    </div>
  )
}
